import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { Input } from '@multaqa/ui/components/ui/input'
import { Label } from '@multaqa/ui/components/ui/label'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Switch } from '@multaqa/ui/components/ui/switch'
import { useState } from 'react'
import type { PositionResource, PositionWriteInput } from './api'
import { useCreatePosition, useUpdatePosition } from './api'
import { localizedValues } from './company-formatters'

export function CompanyPositionDialog({
  position,
  groupId,
  companyId,
  open,
  onOpenChange,
}: {
  position: PositionResource | null
  groupId: string
  companyId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const create = useCreatePosition()
  const update = useUpdatePosition()
  const [nameEn, setNameEn] = useState(position?.name.en ?? '')
  const [nameAr, setNameAr] = useState(position?.name.ar ?? '')
  const [code, setCode] = useState(position?.code ?? '')
  const [active, setActive] = useState(position?.is_active ?? true)
  const [message, setMessage] = useState<string | null>(null)
  const mutation = position ? update : create

  function save() {
    const name = localizedValues(nameEn, nameAr)
    if (Object.keys(name).length === 0) return setMessage(t('companies.positions.nameRequired'))
    if (!code.trim()) return setMessage(t('companies.positions.codeRequired'))
    const input: PositionWriteInput = { name, code: code.trim().toUpperCase(), is_active: active }
    const options = {
      onSuccess: () => {
        onOpenChange(false)
        toast.success(t('state.saved'))
      },
      onError: () => setMessage(t('companies.positions.saveFailed')),
    }
    if (position) update.mutate({ groupId, companyId, positionId: position.id, input }, options)
    else create.mutate({ groupId, companyId, input }, options)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeLabel={t('action.cancel')}
        className="max-w-lg"
        data-testid="company-position-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t(position ? 'companies.positions.editTitle' : 'companies.positions.createTitle')}
          </DialogTitle>
          <DialogDescription>{t('companies.positions.subtitle')}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            save()
          }}
          noValidate
        >
          <DialogBody className="grid gap-4">
            {message ? (
              <p className="text-small text-danger" role="alert">
                {message}
              </p>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="company-position-name-en"
                label={t('companies.positions.nameEn')}
                value={nameEn}
                dir="ltr"
                onChange={setNameEn}
              />
              <TextField
                id="company-position-name-ar"
                label={t('companies.positions.nameAr')}
                value={nameAr}
                dir="rtl"
                onChange={setNameAr}
              />
            </div>
            <TextField
              id="company-position-code"
              label={t('companies.positions.code')}
              value={code}
              dir="ltr"
              onChange={(value) => setCode(value.toUpperCase())}
            />
            <div className="flex min-h-8 items-center justify-between gap-3">
              <Label htmlFor="company-position-active">{t('companies.positions.active')}</Label>
              <Switch id="company-position-active" checked={active} onCheckedChange={setActive} />
            </div>
          </DialogBody>
          <DialogFooter showCloseButton={false}>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t('action.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
              disabled={mutation.isPending}
              data-testid="company-position-submit"
            >
              {t(position ? 'companies.positions.submitEdit' : 'companies.positions.submitCreate')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function TextField({
  id,
  label,
  value,
  dir,
  onChange,
}: {
  id: string
  label: string
  value: string
  dir: 'ltr' | 'rtl'
  onChange: (value: string) => void
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} dir={dir} onChange={(event) => onChange(event.target.value)} />
    </div>
  )
}
